import { RefObject } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Card } from '@mint-lib/tw-components';

import { useDataFileContext } from '../../contexts/DataFileContext/useDataFileContext.js';
import {
  useActivitiesListQuery,
  useDataFilePreview,
  useMediaPlansListQuery,
  usePlatformsOfActivity,
  useUpdateDataFileContext,
} from '../../queries/index.js';
import { Combobox } from '../atoms/Combobox.jsx';

export const ContextFieldsV2 = ({
  ref,
  isUploadStep,
}: {
  ref: RefObject<HTMLDivElement | null>;
  isUploadStep: boolean;
}) => {
  const { t } = useTranslation('@myn/import-hub');

  const { dataFileId } = useDataFileContext();

  const {
    data: { context, granularityLevel },
  } = useDataFilePreview<'V2'>(dataFileId);

  const shouldShowPlatformField = granularityLevel === 'pricing_item';

  const { data: mediaPlansList = [], isLoading: mediaPlansListIsLoading } =
    useMediaPlansListQuery();

  const { data: activitiesList = [], isLoading: activitiesListIsLoading } =
    useActivitiesListQuery(context.mediaPlanId);

  const {
    data: platformsOfActivity = [],
    isLoading: platformsOfActivityIsLoading,
    noOptionsText: platformsNoOptionsText,
  } = usePlatformsOfActivity(context.activityId as string, granularityLevel);

  const { mutate: updateTableDataFileContext } =
    useUpdateDataFileContext(dataFileId);

  const handleFieldChange =
    (fieldName: keyof typeof context) =>
    (value: { id: string | number; name: string }) => {
      const payload = {
        ...context,
        [fieldName]: value?.id || value?.name,
        ...(fieldName === 'mediaPlanId' && {
          mediaPlanName: value?.name,
          activityId: null,
          activityName: null,
          platformName: null,
        }),
        ...(fieldName === 'activityId' && {
          activityName: value?.name,
          platformName: null,
        }),
      } as const;
      updateTableDataFileContext({
        id: dataFileId,
        payload,
      });
    };

  const platformNameFieldValue = platformsOfActivity.find(
    ({ id }) => id === context.platformName,
  );

  return (
    <Card
      ref={ref}
      className="mb-3"
      contentClassName="flex items-center shrink-0 gap-4 p-3"
    >
      <Combobox
        data-dd-action-name="Context panel: select media plan"
        label={t('Media plan*')}
        placeholder={t('Select')}
        inputValue={context.mediaPlanName || null}
        onChange={handleFieldChange('mediaPlanId')}
        options={mediaPlansList}
        getOptionLabel={(option) => option.name}
        getOptionValue={(option) => String(option.id)}
        isLoading={mediaPlansListIsLoading}
        withSearch
        size="small"
        color={!isUploadStep && !context.mediaPlanName ? 'warning' : 'default'}
        className="min-w-[280px]"
        inputProps={{ autoComplete: 'off' }}
      />
      <Combobox
        data-dd-action-name="Context panel: select activity"
        label={t('Activity*')}
        placeholder={t('Select')}
        inputValue={context.activityName || null}
        onChange={handleFieldChange('activityId')}
        options={activitiesList}
        getOptionLabel={(option) => option.name}
        getOptionValue={(option) => String(option.id)}
        isLoading={activitiesListIsLoading}
        withSearch
        disabled={!context.mediaPlanId}
        size="small"
        color={!isUploadStep && !context.activityName ? 'warning' : 'default'}
        className="min-w-[280px]"
        inputProps={{ autoComplete: 'off' }}
      />
      {shouldShowPlatformField && (
        <Combobox
          data-dd-action-name="Context panel: select platform"
          label={t('Platform*')}
          placeholder={t('Select')}
          inputValue={platformNameFieldValue?.name || null}
          onChange={handleFieldChange('platformName')}
          options={platformsOfActivity}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => String(option.id)}
          isLoading={platformsOfActivityIsLoading}
          noOptionsText={platformsNoOptionsText}
          withSearch
          disabled={!context.activityId}
          size="small"
          color={!isUploadStep && !context.platformName ? 'warning' : 'default'}
          className="min-w-[280px]"
          inputProps={{ autoComplete: 'off' }}
        />
      )}
    </Card>
  );
};
